import { useRef, useState } from 'react';
import { Download, FileText, Loader2, Paperclip, ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import type { AttachmentView } from '@vwt/contracts';
import { Badge, Button, cn } from '@vwt/ui';
import { api, getCsrfToken, newIdempotencyKey, useApiMutation, useApiQuery, POLL_ACTIVE, POLL_PENDING } from '../lib/api.js';
import { toastError } from '../lib/errors.js';
import { formatBytes } from '../lib/format.js';
import { useTranslation } from '../lib/i18n.js';
import { apiUrl, fetchCredentials } from '../lib/api-base.js';
import { SectionTitle } from './common.js';
import { MediaThumbnail, mediaItemsOf, toMediaItem, useMediaViewer, type ViewableFile } from './media-viewer.js';

export { formatBytes };

export const MAX_UPLOAD_MIB = 25;
export const MAX_FILES_PER_BATCH = 10;

/** Two-step upload: authorized intent, then the raw bytes into quarantine. */
export function useUploader(parentKind: 'REQUEST' | 'WORK_ITEM', parentId: string) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState<string | null>(null);

  const upload = async (files: File[]): Promise<AttachmentView[]> => {
    if (files.length > MAX_FILES_PER_BATCH) {
      toast.error(t('attachments.tooMany', { max: MAX_FILES_PER_BATCH }));
      return [];
    }
    const done: AttachmentView[] = [];
    for (const file of files) {
      if (file.size > MAX_UPLOAD_MIB * 1024 * 1024) {
        toast.error(t('attachments.tooLarge', { name: file.name, max: MAX_UPLOAD_MIB }));
        continue;
      }
      if (file.size === 0) continue;
      setUploading(file.name);
      try {
        const intent = await api.call('createUploadIntent', {
          body: { parentKind, parentId, filename: file.name, contentType: file.type || 'application/octet-stream', byteSize: file.size },
          idempotencyKey: newIdempotencyKey(),
        });
        const csrf = getCsrfToken();
        const response = await fetch(apiUrl(intent.data.uploadUrl), {
          method: 'PUT',
          credentials: fetchCredentials,
          headers: { 'Content-Type': 'application/octet-stream', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
          body: file,
        });
        const payload = (await response.json().catch(() => null)) as { data?: AttachmentView; error?: { message: string } } | null;
        if (!response.ok || !payload?.data) throw new Error(payload?.error?.message ?? `Upload failed (${response.status})`);
        done.push(payload.data);
      } catch (error) {
        toastError(error);
      }
    }
    setUploading(null);
    if (done.length) toast.success(t('attachments.uploaded', { count: done.length }));
    return done;
  };

  return { upload, uploading };
}

export function AttachButton({ onFiles, disabled, label }: { onFiles: (files: File[]) => void; disabled?: boolean; label?: string }) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) onFiles(files);
        }}
      />
      <Button size="sm" variant="ghost" onClick={() => input.current?.click()} disabled={disabled} aria-label={label ?? t('attachments.add')}>
        <Paperclip /> {label ?? t('attachments.add')}
      </Button>
    </>
  );
}

function ScanBadge({ state, detail }: { state: AttachmentView['scanState']; detail?: string | null }) {
  const { t } = useTranslation();
  if (state === 'AVAILABLE') return null;
  return state === 'PENDING' ? (
    <Badge tone="outline">
      <Loader2 className="animate-spin" /> {t('attachments.scanning')}
    </Badge>
  ) : (
    <Badge tone="danger" title={detail ?? undefined}>
      <ShieldAlert /> {t('attachments.rejected')}
    </Badge>
  );
}

const iconButtonClass = 'inline-flex size-7 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-fg-subtle hover:bg-muted hover:text-fg disabled:opacity-50';

/** Extension shown on the tile of a file that has no visual preview. */
function extensionOf(filename: string): string | null {
  const dot = filename.lastIndexOf('.');
  return dot > 0 && dot < filename.length - 1 ? filename.slice(dot + 1).slice(0, 5).toUpperCase() : null;
}

/** One file card: a visual preview that opens the viewer, or a download tile, plus the file facts and actions. */
function AttachmentCard({ file, onOpen, onRemove, removing }: { file: AttachmentView; onOpen: () => void; onRemove: () => void; removing: boolean }) {
  const { t } = useTranslation();
  const item = toMediaItem(file);
  const extension = extensionOf(file.filename);
  const downloadHref = file.downloadUrl ? apiUrl(`${file.downloadUrl}?download=1`) : null;
  return (
    <li className="relative flex min-w-0 flex-col overflow-hidden rounded-[var(--radius)] border border-border bg-surface" data-testid="attachment-card">
      {item ? (
        <div className="relative">
          <MediaThumbnail item={item} onOpen={onOpen} className="aspect-[4/3] w-full" />
          {item.kind === 'video' ? <span className="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-white uppercase">{t('attachments.video')}</span> : null}
        </div>
      ) : downloadHref ? (
        <a href={downloadHref} className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-1 bg-muted text-fg-subtle hover:text-fg" aria-label={t('attachments.download')} title={t('attachments.download')}>
          <FileText className="size-7" />
          {extension ? <span className="text-[11px] font-semibold tracking-wide">{extension}</span> : null}
        </a>
      ) : (
        <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-1.5 bg-muted text-fg-subtle">
          {file.scanState === 'PENDING' ? <Loader2 className="size-6 animate-spin" aria-hidden /> : <ShieldAlert className="size-6 text-danger" aria-hidden />}
          <ScanBadge state={file.scanState} detail={file.scanDetail} />
        </div>
      )}
      <div className="flex items-center gap-1 px-2 py-1.5">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium" title={file.filename}>
            {file.filename}
          </p>
          <p className="truncate text-[11px] text-fg-subtle" title={file.uploadedBy?.displayName}>
            {[formatBytes(file.byteSize), file.uploadedBy?.displayName].filter(Boolean).join(' · ')}
          </p>
        </div>
        {downloadHref ? (
          <a href={downloadHref} className={iconButtonClass} aria-label={t('attachments.download')} title={t('attachments.download')}>
            <Download className="size-3.5" />
          </a>
        ) : null}
        <button type="button" className={cn(iconButtonClass, 'hover:text-danger')} aria-label={t('attachments.remove')} title={t('attachments.remove')} onClick={onRemove} disabled={removing}>
          <X className="size-3.5" />
        </button>
      </div>
    </li>
  );
}

/** Files on a record: a card grid; images and videos open in the viewer, everything else downloads. */
export function AttachmentsPanel({ parentKind, parentId, canUpload = true }: { parentKind: 'REQUEST' | 'WORK_ITEM'; parentId: string; canUpload?: boolean }) {
  const { t } = useTranslation();
  const viewer = useMediaViewer();
  const [dragging, setDragging] = useState(false);
  const list = useApiQuery(
    'listAttachments',
    { query: { parentKind, parentId } },
    { refetchInterval: (q) => ((q.state.data?.data ?? []).some((a) => a.scanState === 'PENDING') ? POLL_PENDING : POLL_ACTIVE) },
  );
  const remove = useApiMutation('archiveAttachment', { invalidate: ['listAttachments', 'listComments'] });
  const { upload, uploading } = useUploader(parentKind, parentId);
  const files = list.data?.data ?? [];
  const media = mediaItemsOf(files);
  const refresh = () => void list.refetch();

  return (
    <section
      onDragOver={(e) => {
        if (!canUpload) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (!canUpload) return;
        e.preventDefault();
        setDragging(false);
        void upload(Array.from(e.dataTransfer.files)).then(refresh);
      }}
      className={dragging ? 'rounded-[var(--radius)] outline-2 outline-dashed outline-accent' : undefined}
    >
      <SectionTitle actions={canUpload ? <AttachButton onFiles={(f) => void upload(f).then(refresh)} disabled={!!uploading} /> : null}>
        {t('attachments.title')} {files.length ? `· ${files.length}` : ''}
      </SectionTitle>
      {uploading ? (
        <p className="mb-2 flex items-center gap-2 text-xs text-fg-muted">
          <Loader2 className="size-3.5 animate-spin" /> {t('attachments.uploading', { name: uploading })}
        </p>
      ) : null}
      {files.length === 0 ? (
        <p className="text-sm text-fg-subtle">{t('attachments.empty')}</p>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="attachment-list">
          {files.map((file) => (
            <AttachmentCard
              key={file.id}
              file={file}
              removing={remove.isPending && remove.variables?.params.id === file.id}
              onOpen={() => viewer.open(media, media.findIndex((m) => m.id === file.id))}
              onRemove={async () => {
                try {
                  await remove.mutateAsync({ params: { id: file.id } });
                  toast.success(t('attachments.removed'));
                } catch (error) {
                  toastError(error);
                }
              }}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/** Compact file chip used inside comments and the comment composer. */
export function AttachmentChip({ file, onRemove }: { file: ViewableFile; onRemove?: () => void }) {
  const { t } = useTranslation();
  const href = file.scanState === 'AVAILABLE' ? apiUrl(`/api/v1/attachments/${file.id}/content?download=1`) : null;
  const body = (
    <>
      <Paperclip className="size-3.5 shrink-0" />
      <span className="max-w-48 truncate">{file.filename}</span>
      <span className="text-fg-subtle">{formatBytes(file.byteSize)}</span>
      {file.scanState === 'PENDING' ? <Loader2 className="size-3 animate-spin" aria-label={t('attachments.scanning')} /> : null}
      {file.scanState === 'REJECTED' ? <ShieldAlert className="size-3 text-danger" aria-label={t('attachments.rejected')} /> : null}
    </>
  );
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs">
      {href ? (
        <a href={href} className="inline-flex items-center gap-1.5 hover:underline">
          {body}
        </a>
      ) : (
        body
      )}
      {onRemove ? (
        <button onClick={onRemove} aria-label={t('attachments.remove')} className="text-fg-subtle hover:text-fg">
          <X className="size-3" />
        </button>
      ) : null}
    </span>
  );
}

/** Files on a comment: images and videos as thumbnails that open the viewer, the rest as chips. */
export function CommentAttachments({ files }: { files: ViewableFile[] }) {
  const viewer = useMediaViewer();
  if (!files.length) return null;
  const media = mediaItemsOf(files);
  return (
    <div className="mt-2 flex flex-wrap items-end gap-1.5" data-testid="comment-attachments">
      {files.map((file) => {
        const index = media.findIndex((m) => m.id === file.id);
        return index >= 0 ? (
          <div key={file.id} className="overflow-hidden rounded-[var(--radius-sm)] border border-border">
            <MediaThumbnail item={media[index]!} onOpen={() => viewer.open(media, index)} className="h-24 w-32" />
          </div>
        ) : (
          <AttachmentChip key={file.id} file={file} />
        );
      })}
    </div>
  );
}
